import { AttemptDetails, ConceptTag } from '../types';

export type UserSession = {
  attemptId: string;
  questionId?: string;
  socketId?: string;
  socketIds?: string[]; // Array of all socketIds for this attempt
  lastMessageTime?: number;
  attemptDetails?: AttemptDetails | null;
  status?: string | null;
  attemptCreatedAt?: number;
  attemptUpdatedAt?: number;
  hasConversation?: boolean;
  hasImplicitAttempts?: boolean;
  questionUrl?: string;
  solutionUrl?: string;
  conceptTags?: ConceptTag[];
  conceptMetadata?: {
    subject?: string | null;
    parsedAt?: number | null;
    confidence?: number | null;
    updatedAt?: number | null;
    createdAt?: number | null;
  } | null;
};

export const toMs = (value: any): number | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const num = Number(value);
  if (!Number.isNaN(num) && Number.isFinite(num)) return num;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? undefined : parsed;
};

const sortSessionValue = (session: UserSession) => {
  const candidates = [session.lastMessageTime, session.attemptUpdatedAt, session.attemptCreatedAt];
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }
  }
  return 0;
};

export const normalizeSessions = (data: any): UserSession[] => {
  if (!Array.isArray(data)) return [];
  return data
    .map((entry: any) => {
      const attemptId = String(entry?.attemptId || entry?._id || entry?.socketId || '').trim();
      if (!attemptId) return null;
      const questionId = entry?.questionId ? String(entry.questionId).trim() : '';
      const attemptDetails: AttemptDetails | null = entry?.attemptDetails || null;
      const attemptCreatedAt = toMs(entry?.attemptCreatedAt ?? entry?.createdAt);
      const attemptUpdatedAt = toMs(entry?.attemptUpdatedAt ?? entry?.updatedAt);
      const lastMessageTime = toMs(entry?.lastMessageTime);
      const derivedLast = lastMessageTime ?? attemptUpdatedAt ?? attemptCreatedAt;
      const status = entry?.status ? String(entry.status) : (attemptDetails?.resolvedStatus || undefined);
      const questionUrl = entry?.questionUrl ? String(entry.questionUrl).trim() : '';
      const solutionUrl = entry?.solutionUrl ? String(entry.solutionUrl).trim() : '';

      // Handle socketId as either array or single value
      let socketIds: string[] | undefined;
      let socketId: string | undefined;

      if (Array.isArray(entry?.socketIds)) {
        const filtered = entry.socketIds.filter((id: any) => id).map((id: any) => String(id).trim());
        socketIds = filtered.length > 0 ? filtered : undefined;
        socketId = socketIds && socketIds.length > 0 ? socketIds[socketIds.length - 1] : undefined; // Latest socketId
      } else if (Array.isArray(entry?.socketId)) {
        const filtered = entry.socketId.filter((id: any) => id).map((id: any) => String(id).trim());
        socketIds = filtered.length > 0 ? filtered : undefined;
        socketId = socketIds && socketIds.length > 0 ? socketIds[socketIds.length - 1] : undefined; // Latest socketId
      } else if (entry?.socketId) {
        socketId = String(entry.socketId).trim();
        socketIds = [socketId];
      } else if (entry?.socketIds) {
        socketId = String(entry.socketIds).trim();
        socketIds = [socketId];
      }

      return {
        attemptId,
        socketId,
        socketIds,
        lastMessageTime: derivedLast,
        status,
        attemptDetails,
        attemptCreatedAt,
        attemptUpdatedAt,
        hasConversation: Boolean(entry?.hasConversation),
        hasImplicitAttempts: Boolean(entry?.hasImplicitAttempts),
        questionUrl: questionUrl || undefined,
        solutionUrl: solutionUrl || undefined,
        questionId: questionId || undefined,
        conceptTags: Array.isArray(entry?.conceptTags) ? entry.conceptTags : undefined,
        conceptMetadata: entry?.conceptMetadata || undefined,
      } as UserSession;
    })
    .filter((item): item is UserSession => Boolean(item?.attemptId))
    .sort((a, b) => sortSessionValue(b) - sortSessionValue(a));
};

export const countValidSessions = (sessions: UserSession[]): number => (
  sessions.reduce((count, session) => (
    session?.attemptDetails?.isValid ? count + 1 : count
  ), 0)
);